import { datadogRum } from '@datadog/browser-rum'

let isInitialized = false

const RUM_IGNORED_FETCHES = [
  '_boost/browser-logs',
  'canny.io',
  'customer.io',
  'facebook.com',
  'freshdesk.com',
  'fullstory.com',
  'google-analytics.com',
  'google.com',
  'intercom.io',
  'mapbox.com',
  'small.chat',
  'usefathom.com',
  'vitally.io',
  'zendesk.com',
]

/**
 * Whether to discard a resource event.
 *
 * @param {Object} event - The resource event
 * @param {Object} _context - The context
 *
 * @returns {boolean}
 */
function shouldDiscard(event, _context) {
  if (event.type === 'resource' && ['fetch', 'xhr'].includes(event.resource.type)) {
    return RUM_IGNORED_FETCHES.some(
      (host) => typeof event.resource.url === 'string' && event.resource.url.includes(host)
    )
  }

  return false
}

/**
 * Initialize Datadog RUM when config is present.
 * Call once from app entry (app.js).
 */
export function initDatadogRum() {
  const applicationId = window.ARYEO_DATADOG_RUM_APPLICATION_ID
  const clientToken = window.ARYEO_DATADOG_RUM_CLIENT_TOKEN

  if (!applicationId || !clientToken) {
    return
  }

  if (isInitialized) {
    return
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site: window.ARYEO_DATADOG_RUM_SITE,
    service: window.ARYEO_DATADOG_RUM_SERVICE,
    env: window.ARYEO_DATADOG_RUM_ENV,
    version: window.ARYEO_DATADOG_RUM_VERSION,
    sessionSampleRate: parseInt(window.ARYEO_DATADOG_RUM_SESSION_SAMPLE_RATE, 10),
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true, // We track views manually in app.js through startRumView() on router 'navigate' event.
    beforeSend: (event, context) => !shouldDiscard(event, context),
  })

  if (window.ARYEO_DATADOG_RUM_USER) {
    datadogRum.setUser(window.ARYEO_DATADOG_RUM_USER)
  }

  isInitialized = true
}

/**
 * Get the view name from an Inertia page object.
 * If the page object has a component property, return it.
 * Otherwise, return the window.location.pathname.
 *
 * @param {Object} page - Inertia Page Object
 *
 * @returns string
 */
export function getViewNameFromPage(page) {
  return page?.component ?? window.location.pathname
}

/**
 * Get the context from a view name.
 * The view name is in the format: UserType/Domain/Process/Component
 *
 * @param {String} viewName - The view name (e.g. Customer/OrderForms/OrderForm/ShowOrderFormAppointmentStep)
 *
 * @returns {Object}
 * @property {String} user_type - The type of user (Customer or Company)
 * @property {String} domain - The domain of the view (OrderForms)
 * @property {String} process - The process of the view (OrderForm)
 */
function getContextFromViewName(viewName) {
  if (typeof viewName !== 'string' || !viewName.includes('/')) {
    return {}
  }

  const [userType, domain, process] = viewName.split('/')

  return {
    user_type: userType,
    domain,
    process,
  }
}

/**
 * Remove undefined, null, and empty values from a context object.
 *
 * @param {Record<string, unknown>} context - The context object
 *
 * @returns {Record<string, unknown>}
 */
export function cleanContext(context) {
  return Object.fromEntries(
    Object.entries(context).filter(([, v]) => v !== undefined && v !== null && v !== '')
  )
}

/**
 * Start a RUM view (for Inertia navigation).
 * Call on router 'navigate' with the resolved page component name.
 *
 * @param {string} viewName - View name (e.g. Inertia component path or route name)
 * @param {Record<string, unknown>} [context] - Optional view context
 */
export function startRumView(viewName, context = {}) {
  if (!isInitialized) {
    return
  }

  try {
    const viewContext = getContextFromViewName(viewName)

    datadogRum.startView({
      name: viewName,
      context: cleanContext({ ...viewContext, ...context }),
    })
  } catch (error) {
    console.error('Error starting RUM view', error)
  }
}

/**
 * Add a custom RUM action (for funnels and business events).
 *
 * @param {string} name - Action name (e.g. 'order_form.step.address')
 * @param {Record<string, string|number|boolean|undefined>} [context] - Action context (no PII)
 */
export function addRumAction(name, context = {}) {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.addAction(name, cleanContext(context))
  } catch (error) {
    console.error('Error adding RUM action', error)
  }
}

export function addRumError(error, context) {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.addError(error, context)
  } catch (error) {
    console.error('Error adding RUM error', error)
  }
}

/**
 * Stop the current RUM session and remove the user.
 */
export function stopRumSession() {
  if (!isInitialized) {
    return
  }

  try {
    datadogRum.stopSession()
    datadogRum.clearUser()
  } catch (error) {
    console.error('Error stopping RUM session', error)
  }
}

/**
 * Whether RUM is currently initialized (for conditional tracking in components).
 */
export function isDatadogRumInitialized() {
  return isInitialized
}
